import type {
  Deductible,
  OutOfPocketMax,
  PatientNotes,
  Plan,
  Payor,
  Vob,
  VobClassification,
} from '@finpay-estimation/estimation-types';

import type { AvailityVobEntity } from './availity-service';
import { AVAILITY_VOB_SOURCE_ID } from './vob-source';
import { withVobPlanCodeAsString } from './vob-plan-code';

export interface BuildVobFromAvailityContext {
  patientId: number;
  clientId: number;
  facilityId: number;
  payorId: number;
  payorPlanId: number;
  memberId?: string;
  webformNotes?: string;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : undefined;
}

function buildDeductible(
  total?: number,
  remaining?: number
): Deductible | undefined {
  if (total == null && remaining == null) {
    return undefined;
  }
  const totalAmount = total ?? 0;
  const remainingAmount = remaining ?? 0;
  return {
    total: totalAmount,
    met: totalAmount - remainingAmount,
    remaining: remainingAmount,
  };
}

function buildOopm(
  total?: number,
  remaining?: number,
  isDeductibleIncluded?: boolean
): OutOfPocketMax | undefined {
  if (total == null && remaining == null) {
    return undefined;
  }
  const totalAmount = total ?? 0;
  const remainingAmount = remaining ?? 0;
  return {
    total: totalAmount,
    met: totalAmount - remainingAmount,
    remaining: remainingAmount,
    isDeductibleIncluded: Boolean(isDeductibleIncluded),
  };
}

function mapLegacyClassifications(
  legacy: unknown
): VobClassification[] | undefined {
  if (!Array.isArray(legacy)) {
    return undefined;
  }

  const mapped = legacy
    .map((item) => {
      const row = asRecord(item);
      if (!row) {
        return null;
      }
      const selected = asRecord(row.selectedClassification);
      const vobClassificationId = Number(
        row.vobClassificationId ?? selected?.vobClassificationId ?? 0
      );
      if (!vobClassificationId) {
        return null;
      }
      return {
        vobClassificationId,
        copayType: String(row.copayType ?? row.coPaySelection ?? ''),
        coInsurance:
          row.coInsurance != null ? Number(row.coInsurance) : undefined,
        coPay: row.coPay != null ? Number(row.coPay) : undefined,
        maxDays: row.maxDays != null ? Number(row.maxDays) : undefined,
      } as VobClassification;
    })
    .filter((item): item is VobClassification => item != null);

  return mapped.length > 0 ? mapped : undefined;
}

function resolveNotes(
  availity: AvailityVobEntity,
  webformNotes?: string
): PatientNotes[] | undefined {
  const trimmed = webformNotes?.trim();
  if (trimmed) {
    return [{ noteText: trimmed, noteDt: new Date().toISOString() }];
  }
  if (Array.isArray(availity.notes)) {
    return availity.notes as PatientNotes[];
  }
  return undefined;
}

export function buildVobFromAvaility(
  availity: AvailityVobEntity,
  context: BuildVobFromAvailityContext
): Vob {
  const currentYear = new Date().getFullYear();
  const legacyPayor = asRecord(availity.payer) ?? asRecord(availity.payor);
  const legacyPlan = asRecord(availity.plan);

  const payor: Payor = {
    ...legacyPayor,
    payorId: context.payorId,
    payorPlanId: context.payorPlanId,
    inNetwork: legacyPayor?.inNetwork !== false,
  };

  const plan: Plan = {
    ...legacyPlan,
    payorPlanId:
      context.payorPlanId > 0
        ? context.payorPlanId
        : Number(legacyPlan?.payorPlanId ?? 0) || undefined,
  };

  const policyNum =
    context.memberId?.trim() ||
    (typeof availity.policyNum === 'string' ? availity.policyNum.trim() : '');
  const groupNum =
    typeof availity.groupNum === 'string' ? availity.groupNum.trim() : '';

  const vob: Vob = {
    patientId: context.patientId,
    client: { clientId: context.clientId },
    facility: { facilityId: context.facilityId },
    payor,
    plan,
    vobSourceId:
      typeof availity.vobSourceId === 'number'
        ? availity.vobSourceId
        : AVAILITY_VOB_SOURCE_ID,
    isValid: false,
    digitalVerificationMethod: true,
    liveVOB: Boolean(availity.liveVOB ?? true),
    activePolicy: Boolean(availity.activePolicy ?? true),
    isCarryover: Boolean(availity.isCarryover ?? false),
    isCopayReset: Boolean(availity.isCopayReset ?? false),
    policyBeginDate:
      typeof availity.policyBeginDate === 'string' && availity.policyBeginDate
        ? availity.policyBeginDate
        : `${currentYear}-01-01`,
    policyEndDate:
      typeof availity.policyEndDate === 'string' && availity.policyEndDate
        ? availity.policyEndDate
        : `${currentYear}-12-31`,
    innDeductible:
      (availity.innDeductible as Deductible | undefined) ??
      buildDeductible(
        availity.inNetwDeductible as number | undefined,
        availity.inNetwDeductibleRemaining as number | undefined
      ),
    innOopm:
      (availity.innOopm as OutOfPocketMax | undefined) ??
      buildOopm(
        availity.inNetwOopMax as number | undefined,
        availity.inNetwOopMaxRemaining as number | undefined,
        availity.inNetwOopIncluded as boolean | undefined
      ),
    innFamilyDeductible:
      (availity.innFamilyDeductible as Deductible | undefined) ??
      buildDeductible(
        availity.inNetwFamilyDeductible as number | undefined,
        availity.inNetwFamilyDeductibleRemaining as number | undefined
      ),
    innFamilyOopm:
      (availity.innFamilyOopm as OutOfPocketMax | undefined) ??
      buildOopm(
        availity.inNetwFamilyOopMax as number | undefined,
        availity.inNetwFamilyOopMaxRemaining as number | undefined,
        availity.inNetwOopIncluded as boolean | undefined
      ),
    innClassifications:
      (availity.innClassifications as VobClassification[] | undefined) ??
      mapLegacyClassifications(availity.inNetwVobClassifications),
    oonDeductible:
      (availity.oonDeductible as Deductible | undefined) ??
      buildDeductible(
        availity.ooNetwDeductible as number | undefined,
        availity.ooNetwDeductibleRemaining as number | undefined
      ),
    oonOopm:
      (availity.oonOopm as OutOfPocketMax | undefined) ??
      buildOopm(
        availity.ooNetwOopMax as number | undefined,
        availity.ooNetwOopMaxRemaining as number | undefined,
        availity.ooNetwOopIncluded as boolean | undefined
      ),
    oonFamilyDeductible:
      (availity.oonFamilyDeductible as Deductible | undefined) ??
      buildDeductible(
        availity.ooNetwFamilyDeductible as number | undefined,
        availity.ooNetwFamilyDeductibleRemaining as number | undefined
      ),
    oonFamilyOopm:
      (availity.oonFamilyOopm as OutOfPocketMax | undefined) ??
      buildOopm(
        availity.ooNetwFamilyOopMax as number | undefined,
        availity.ooNetwFamilyOopMaxRemaining as number | undefined,
        availity.ooNetwOopIncluded as boolean | undefined
      ),
    oonClassifications:
      (availity.oonClassifications as VobClassification[] | undefined) ??
      mapLegacyClassifications(availity.ooNetwVobClassifications),
    notes: resolveNotes(availity, context.webformNotes),
    employer: availity.employer as Vob['employer'],
    planSponsor: availity.planSponsor as Vob['planSponsor'],
    subscriber: availity.subscriber as Vob['subscriber'],
    patient: availity.patient as Vob['patient'],
    insuranceType: availity.insuranceType as string | undefined,
    insuranceTypeCode: availity.insuranceTypeCode as string | undefined,
    rateAsPayorPlan: availity.rateAsPayorPlan as Vob['rateAsPayorPlan'],
    sourceJson: availity.sourceJson,
  };

  if (groupNum) {
    vob.groupNum = groupNum;
  }
  if (policyNum) {
    vob.policyNum = policyNum;
  }

  return withVobPlanCodeAsString(vob);
}
